// src/components/hooks/useRegisterCliente.ts

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

import {
  signUpUser,
  loginUser,
  updateUserMetadata,
  addPerfil,
  addConsentimientoCliente,
} from '@/components/utils/authHelpers';
import { PASSWORD_MIN, passwordValida } from '@/components/utils/password';

const CURRENT_POLICY_VERSION = 'v1.0-2025-09';
const CURRENT_POLICY_TEXT =
  'Acepto el tratamiento de mis datos personales según la Política de Privacidad vigente.';

export interface ClienteForm {
  nombre: string;
  telefono: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const useRegisterCliente = () => {
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [tienePerfilCliente, setTienePerfilCliente] = useState(false);
  const [requirePassword, setRequirePassword] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ============================================================
     RESET
  ============================================================ */
  const reset = useCallback(() => {
    setCheckingEmail(false);
    setEmailExists(false);
    setTienePerfilCliente(false);
    setRequirePassword(false);
    setLoading(false);
  }, []);

  /* ============================================================
     CHECK EMAIL
  ============================================================ */
  const checkEmail = useCallback(async (email: string) => {
    const clean = email.trim().toLowerCase();

    if (!clean) return false;

    setCheckingEmail(true);
    try {
      const { data, error } = await supabase
        .from('vista_cliente_detalle')
        .select('id_usuario, perfiles')
        .eq('email', clean)
        .maybeSingle();

      if (error) {
        Sentry.captureException(error, { extra: { email: clean } });
        return false;
      }

      if (!data) {
        setEmailExists(false);
        setTienePerfilCliente(false);
        return false;
      }

      const esCliente =
        data.perfiles && data.perfiles.toLowerCase().includes('cliente');

      setEmailExists(true);
      setTienePerfilCliente(esCliente);

      if (esCliente) {
        toast.error('Este correo ya pertenece a un cliente registrado.');
      }

      return true;
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  /* ============================================================
     REGISTER CLIENTE
  ============================================================ */
  const register = useCallback(
    async (form: ClienteForm, consentimiento: boolean, isGoogle: boolean) => {
      setLoading(true);

      try {
        const { nombre, telefono, email, password, confirmPassword } = form;
        const cleanEmail = email.trim().toLowerCase();

        // ================= VALIDACIÓN BÁSICA =================
        if (!cleanEmail || !nombre?.trim() || !telefono?.trim()) {
          toast.error('Todos los campos son obligatorios.');
          return { ok: false };
        }

        let userId = '';

        // ================= GOOGLE (YA AUTENTICADO) =================
        if (isGoogle) {
          const { data, error } = await supabase.auth.getUser();

          if (error || !data?.user) {
            if (error) Sentry.captureException(error);
            toast.error('No se pudo obtener el usuario de Google.');
            return { ok: false };
          }

          userId = data.user.id;

          await updateUserMetadata({
            full_name: nombre.trim(),
            nombre_app: nombre.trim(),
            telefono: telefono.trim(),
            descripcion: '',
          });
        }

        // ================= EMAIL / PASSWORD =================
        if (!isGoogle) {
          const { data: row, error: rowErr } = await supabase
            .from('vista_cliente_detalle')
            .select('id_usuario, perfiles')
            .eq('email', cleanEmail)
            .maybeSingle();

          if (rowErr) {
            Sentry.captureException(rowErr, { extra: { email: cleanEmail } });
            toast.error('No se pudo verificar el usuario.');
            return { ok: false };
          }

          const exists = !!row?.id_usuario;
          const esCliente = row?.perfiles
            ? row.perfiles.toLowerCase().includes('cliente')
            : false;

          if (esCliente) {
            toast.error('Ya tienes perfil cliente.');
            return { ok: false };
          }

          if (exists) {
            if (!password) {
              toast.error('Introduce tu contraseña.');
              return { ok: false };
            }

            const login = await loginUser(cleanEmail, password);
            if (!login.ok || !login.user) {
              toast.error('Credenciales incorrectas.');
              return { ok: false };
            }

            userId = login.user.id;
          } else {
            if (!password || !confirmPassword) {
              toast.error('Debes crear y confirmar tu contraseña.');
              return { ok: false };
            }

            // Misma regla que la UI (utils/password), para que el hook no
            // acepte contraseñas que el formulario ya rechaza
            if (!passwordValida(password)) {
              toast.error(
                `La contraseña debe tener al menos ${PASSWORD_MIN} caracteres, con letras y números.`
              );
              return { ok: false };
            }

            if (password !== confirmPassword) {
              toast.error('Las contraseñas no coinciden.');
              return { ok: false };
            }

            const sign = await signUpUser(cleanEmail, password);
            if (!sign.ok || !sign.user) {
              return { ok: false };
            }

            const login = await loginUser(cleanEmail, password);
            if (!login.ok || !login.user) {
              return { ok: false };
            }

            userId = login.user.id;
          }

          await updateUserMetadata({
            full_name: nombre.trim(),
            nombre_app: nombre.trim(),
            telefono: telefono.trim(),
            descripcion: '',
          });
        }

        // ================= PERFIL CLIENTE (IDEMPOTENTE) =================
        const { data: perfilesAntes, error: perfilesAntesErr } = await supabase
          .from('vista_usuarios_detalle')
          .select('perfil')
          .eq('id_usuario', userId);

        if (perfilesAntesErr) {
          Sentry.captureException(perfilesAntesErr, { extra: { userId } });
          toast.error('No se pudieron verificar los perfiles.');
          return { ok: false };
        }

        const yaEsCliente = (perfilesAntes ?? []).some(
          (p: any) => p.perfil === 'cliente'
        );

        if (!yaEsCliente) {
          await addPerfil(userId, 'cliente');
        }

        // ================= CONSENTIMIENTO =================
        if (consentimiento) {
          await addConsentimientoCliente(
            userId,
            cleanEmail,
            telefono.trim(),
            CURRENT_POLICY_VERSION,
            CURRENT_POLICY_TEXT
          );
        }

        // ================= LOCALSTORAGE =================
        const { data: perfilesDespues, error: perfilesDespuesErr } =
          await supabase
            .from('vista_usuarios_detalle')
            .select('id_perfil, id_empresa, ruta_panel, perfil')
            .eq('id_usuario', userId);

        if (perfilesDespuesErr) {
          Sentry.captureException(perfilesDespuesErr, { extra: { userId } });
          toast.error('No se pudieron cargar los perfiles.');
          return { ok: false };
        }

        localStorage.setItem('id_usuario', userId);
        localStorage.setItem('email', cleanEmail);

        const perfilCliente = (perfilesDespues ?? []).find(
          (p: any) => p.perfil === 'cliente'
        );

        if (perfilCliente) {
          localStorage.setItem('id_perfil', perfilCliente.id_perfil || '');
          localStorage.setItem('id_empresa', perfilCliente.id_empresa || '');
        } else {
          localStorage.removeItem('id_perfil');
          localStorage.removeItem('id_empresa');
        }

        return {
          ok: true,
          userId,
          perfilesCount: (perfilesDespues ?? []).length,
        };
      } catch (err) {
        Sentry.captureException(err);
        toast.error('Error durante el registro.');
        return { ok: false };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    checkingEmail,
    emailExists,
    tienePerfilCliente,
    requirePassword,
    loading,
    setRequirePassword,
    checkEmail,
    register,
    reset,
  };
};